import { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import TextField from '@mui/material/TextField';

/**
 * A text field that prefills the signed-in user's GITHUB login.
 *
 * WHY THIS CANNOT BE A PLAIN DEFAULT. The value feeds GitHub's collaborator
 * API, so it has to be a GitHub username. Backstage's own identity is not one:
 * the microsoft sign-in resolver produces `user:default/ben.koth` from the Entra
 * email local part, so `${{ user.entity.metadata.name }}` would submit
 * "ben.koth" and GitHub would reject it - or worse, match somebody else.
 *
 * There is no mapping between the two identities in the catalog, so the only
 * reliable source is GitHub itself: GET /user with the viewer's own token
 * returns their login.
 *
 * DELIBERATELY NON-BLOCKING. `optional: true` on getCredentials means this will
 * not raise an OAuth popup on a user who has not yet granted GitHub - the field
 * simply stays empty and they type a name, exactly as before. A form that
 * demands a consent dialog before it will render is worse than one that
 * sometimes cannot guess.
 *
 * Prefills only when EMPTY, and only once. Re-running it would fight anyone who
 * deliberately typed a different admin, which is a normal thing to want - the
 * repo admin is not always the person creating the repo.
 */
export const GitHubUserField = (
  props: FieldExtensionComponentProps<string>,
) => {
  const { onChange, formData, schema, required, rawErrors } = props;
  const scmAuth = useApi(scmAuthApiRef);
  const [prefilling, setPrefilling] = useState(false);
  // Guards against a second attempt after the user clears the field: an empty
  // box then means "I want it empty", not "please guess again".
  const attempted = useRef(false);

  // onChange and formData are held in refs rather than being effect deps.
  //
  // rjsf hands down a NEW onChange identity on every render, so listing it as a
  // dependency re-ran this effect constantly. Combined with the `attempted`
  // guard that deadlocked the field: the re-run's cleanup set cancelled, which
  // skipped the in-flight finally, and the guard then blocked any retry - so the
  // helper text sat on "Looking up your GitHub username…" forever.
  const onChangeRef = useRef(onChange);
  const formDataRef = useRef(formData);
  useEffect(() => {
    onChangeRef.current = onChange;
    formDataRef.current = formData;
  }, [onChange, formData]);

  useEffect(() => {
    if (attempted.current || formDataRef.current) {
      return undefined;
    }
    attempted.current = true;
    let cancelled = false;

    (async () => {
      try {
        setPrefilling(true);
        const { token } = await scmAuth.getCredentials({
          url: 'https://github.com',
          // No additionalScope, deliberately. GET /user returns the viewer's own
          // profile on any authenticated token, so asking for `read:user` buys
          // nothing - and with optional: true it actively costs: a session that
          // holds a token WITHOUT that scope returns nothing rather than
          // prompting, so the prefill would silently never fire. Asking for the
          // least makes a cached grant most likely to satisfy it.
          //
          // Never force a sign-in prompt from a form field - see doc comment.
          optional: true,
        });
        if (!token || cancelled) {
          return;
        }
        const res = await fetch('https://api.github.com/user', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/vnd.github+json',
            'X-GitHub-Api-Version': '2022-11-28',
          },
        });
        if (!res.ok || cancelled) {
          return;
        }
        const login = (await res.json())?.login;
        // Re-read through the ref: the user may have typed while this was in
        // flight, and overwriting what they entered would be worse than not
        // guessing at all.
        if (login && !cancelled && !formDataRef.current) {
          onChangeRef.current(login);
        }
      } catch {
        // Soft failure: an unguessable field is still a usable field.
      } finally {
        // UNCONDITIONAL, deliberately. Gating this on `cancelled` is what left
        // the field stuck showing "Looking up…" - React 18 makes a setState on
        // an unmounted component a no-op, so there is nothing to protect
        // against here.
        setPrefilling(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // scmAuth only: this runs once, and everything else is read through a ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scmAuth]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value || undefined);
    },
    [onChange],
  );

  return (
    <TextField
      id={props.idSchema?.$id}
      label={schema.title ?? 'Admin'}
      helperText={
        prefilling
          ? 'Looking up your GitHub username…'
          : (schema.description as string | undefined)
      }
      required={required}
      value={formData ?? ''}
      onChange={handleChange}
      error={Boolean(rawErrors?.length) && !formData}
      margin="normal"
      fullWidth
    />
  );
};
